import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IconChevronRight } from "@tabler/icons-react";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../../../domains/user/hooks/useCurrentUser";
import { apiClient, useApiQuery } from "../../../../services/api";
import { getItemTranslation } from "../../../../domains/app/utils/getItemTranslation";
import {
  formatProfileLink,
  isValidProfileLink,
} from "../../../../domains/user/profileLink";
import { Input } from "../../../ui/Input";
import { Select } from "../../../ui/Select";
import { Button } from "../../../ui/Button";
import Loader from "../../../Loader";
import ProfilePicture from "../../../ProfilePicture";
import CustomIcon from "../../../CustomIcon";
import { displayToast } from "../../../app/AppToast";
import {
  popSettingsView,
  pushSettingsView,
} from "../../../../domains/app/stores/settingsSheetStore";
import { resetAvatarPicker } from "../../../../domains/user/stores/avatarPickerStore";
import { SettingsSubHeader } from "../SettingsSubHeader";

type EditProfileForm = {
  firstName: string;
  lastName: string;
  professionUid: string;
  link: string;
  bio: string;
};

export function EditProfileView(): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const professionsQuery = useApiQuery("/webapp/public/professions", {
    durable: true,
  });
  const professions =
    professionsQuery.state === "success" ? professionsQuery.body : [];

  const { register, handleSubmit, watch, setValue, formState } =
    useForm<EditProfileForm>({
      // Reseed from the loaded user (and after a save reloads it) without an
      // effect — same pattern as onboarding's NameStep.
      values: {
        firstName:
          currentUser === null || currentUser.firstname === null
            ? ""
            : currentUser.firstname,
        lastName:
          currentUser === null || currentUser.lastname === null
            ? ""
            : currentUser.lastname,
        professionUid:
          currentUser === null || currentUser.professionModel === null
            ? ""
            : currentUser.professionModel.uid,
        link:
          currentUser === null || currentUser.link === null
            ? ""
            : currentUser.link,
        bio:
          currentUser === null || currentUser.bio === null
            ? ""
            : currentUser.bio,
      },
    });

  const professionOptions = [...professions]
    .sort((a, b) => getItemTranslation(a).localeCompare(getItemTranslation(b)))
    .map((profession) => ({
      value: profession.uid,
      label: getItemTranslation(profession),
    }));

  const mainSpecialty =
    currentUser === null ? null : currentUser.mainSpecialtyModel;

  async function onSave(data: EditProfileForm): Promise<void> {
    const formattedLink = formatProfileLink(data.link);
    if (formattedLink !== null && !isValidProfileLink(formattedLink)) {
      displayToast(t("Please provide a valid link."), "warning");
      return;
    }
    const profession = professions.find((p) => p.uid === data.professionUid);
    try {
      await apiClient.patch("/webapp/private/users/me", {
        json: {
          firstname: data.firstName,
          lastname: data.lastName,
          bio: data.bio,
          // `undefined` omits the key: an empty field leaves the stored link
          // alone, because neither editor offers a way to clear one.
          link: formattedLink === null ? undefined : formattedLink,
          profession: profession === undefined ? undefined : profession.id,
        },
      });
      await reloadCurrentUser();
      displayToast(t("Profile updated"), "success");
      popSettingsView();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    <form
      className="flex h-full flex-col"
      onSubmit={handleSubmit((data) => {
        void onSave(data);
      })}
    >
      <SettingsSubHeader title={t("Edit your profile")} />

      {professionsQuery.state === "error" ? (
        <p className="px-5 py-4 text-ui-body text-ds-slate-600">
          {professionsQuery.message}
        </p>
      ) : currentUser === null || professionsQuery.state !== "success" ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto scroll-fog-y px-5">
          <button
            type="button"
            onClick={() => {
              resetAvatarPicker();
              pushSettingsView({ view: "editAvatar" });
            }}
            className="mx-auto mb-6 flex w-fit"
          >
            <ProfilePicture
              user={currentUser}
              height={90}
              badge={<CustomIcon iconName="edit_avatar" />}
            />
          </button>

          <h3 className="text-ui-highlight font-semibold text-ds-black">
            {t("Main information")}
          </h3>
          <div className="mt-3 flex flex-col gap-3">
            <Input placeholder={t("First Name")} {...register("firstName")} />
            <Input placeholder={t("Last Name")} {...register("lastName")} />
            <Select
              appearance="field"
              footerAction={null}
              searchOnlyOptions={null}
              value={watch("professionUid")}
              options={professionOptions}
              placeholder={t("Profession")}
              onChange={(option) => {
                setValue("professionUid", option.value, { shouldDirty: true });
              }}
              className="bg-white"
            />
            <button
              type="button"
              onClick={() => {
                pushSettingsView({ view: "mainSpecialty" });
              }}
              className="flex items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3 text-left"
            >
              <span className="flex min-w-0 flex-col">
                <span className="text-ui-caption font-medium text-ds-slate-500">
                  {t("Main specialty")}
                </span>
                <span className="truncate text-ui-body font-medium text-ds-black">
                  {mainSpecialty === null
                    ? ""
                    : getItemTranslation(mainSpecialty)}
                </span>
              </span>
              <IconChevronRight
                size={20}
                stroke={1.5}
                className="shrink-0 text-ds-slate-400"
              />
            </button>
          </div>

          <p className="mt-6 mb-3 text-ui-body font-medium text-ds-slate-600">
            {t("Highlight your web space")}
          </p>
          <div className="flex flex-col gap-3">
            <Input placeholder="example.com" {...register("link")} />
            <textarea
              rows={5}
              placeholder={t("Update your bio")}
              {...register("bio")}
              className="w-full resize-none rounded-2xl border border-ds-slate-300 bg-white px-4 py-3 text-ui-body text-ds-black placeholder:text-ds-slate-400 focus:outline-none"
            />
          </div>
        </div>
      )}

      <div className="shrink-0 px-5 pt-2 pb-6">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!formState.isDirty || formState.isSubmitting}
        >
          {t("Save")}
        </Button>
      </div>
    </form>
  );
}
